import React, { useEffect, useState } from 'react';
import { reportButtonStyle } from '../../shared/ReportShell';
import { readLinks, linkPrinter } from './packingStation';
import { layoutBoxLabel, toZpl } from './boxLabelLayout';
import { availablePrinters, preparePrinter, sendToPrinter, BROWSER_PRINT_DOWNLOAD_URL } from './zebraBrowserPrint';
import CancelButton from '../../shared/CancelButton';

export function boxLabelLink(token) {
  return `${window.location.origin}/b/${token}`;
}

// Printing a Parcel or Package label at a packing station (issue #305): on the printer
// this PC links to the label's size, laid out at that printer's own
// resolution and sent through Zebra Browser Print. A size with no printer
// linked yet asks which printer has that roll, and remembers the answer.
//
// Returns:
//   printLabel(label, size) — prints; resolves to { printer } or null if
//     staff cancelled choosing a printer.
//   printZpl(size, zplFor) — the same for any label: zplFor(dpi) gives the
//     ZPL at the printer's own resolution, as many labels as it holds.
//   problem / problemEl — what went wrong last time, worded for staff, with
//     where to get Browser Print when it isn't running.
//   chooserEl — the "which printer has this roll?" dialog, when it's open.
export function useBoxLabelPrinter() {
  const [asking, setAsking] = useState(null);
  const [problem, setProblem] = useState(null);

  const choosePrinter = (size) => new Promise((resolve) => setAsking({ size, resolve }));

  const printerFor = async (size) => {
    const linked = readLinks()[size.id];
    if (linked) return linked;
    const chosen = await choosePrinter(size);
    if (chosen) linkPrinter(size.id, chosen);
    return chosen;
  };

  const run = async (work) => {
    setProblem(null);
    try {
      return await work();
    } catch (err) {
      setProblem({ code: err.code || 'failed', message: err.response?.data?.message || err.message || 'The label could not be printed' });
      throw err;
    }
  };

  const printZpl = (size, zplFor) => run(async () => {
    const device = await printerFor(size);
    if (!device) return null;
    const { printer, dpi } = await preparePrinter(device);
    await sendToPrinter(printer, zplFor(dpi));
    return { printer };
  });

  const printLabel = (label, size) => printZpl(size, (dpi) => toZpl(layoutBoxLabel({ label, link: boxLabelLink(label.label_token), size, dpi })));

  const problemEl = problem ? (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#b91c1c', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>Label not printed</div>
        <div>{problem.message}</div>
        {problem.code === 'not_running' && (
          <a href={BROWSER_PRINT_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', display: 'inline-block', marginTop: 4 }}>
            Get Zebra Browser Print ↗
          </a>
        )}
      </div>
      <button type="button" onClick={() => setProblem(null)} title="Dismiss" style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
    </div>
  ) : null;

  const chooserEl = asking ? (
    <PrinterChooser
      size={asking.size}
      onDone={(device) => { asking.resolve(device); setAsking(null); }}
    />
  ) : null;

  return { printLabel, printZpl, problem, problemEl, chooserEl };
}

// "Which printer has the 50 × 28 mm roll?" — the Zebra printers Browser
// Print can see from this PC.
function PrinterChooser({ size, onDone }) {
  const [printers, setPrinters] = useState(null);
  const [error, setError] = useState(null);
  const [uid, setUid] = useState('');

  useEffect(() => {
    let live = true;
    availablePrinters()
      .then((list) => { if (live) { setPrinters(list); setUid(list[0]?.uid || ''); } })
      .catch((err) => { if (live) setError(err); });
    return () => { live = false; };
  }, []);

  const chosen = (printers || []).find((p) => p.uid === uid);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 20, width: '100%', maxWidth: 420 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Which printer has the {size.name} roll?</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>This PC will print {size.name} labels on it from now on. Change it any time on Warehouse › Packing Station.</div>
        {!printers && !error && <div style={{ fontSize: 13 }}>Looking for printers...</div>}
        {error && (
          <div style={{ fontSize: 13, color: '#b91c1c' }}>
            {error.message}
            {error.code === 'not_running' && (
              <div style={{ marginTop: 4 }}>
                <a href={BROWSER_PRINT_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>Get Zebra Browser Print ↗</a>
              </div>
            )}
          </div>
        )}
        {printers && printers.length === 0 && (
          <div style={{ fontSize: 13, color: '#b91c1c' }}>Browser Print can't see any Zebra printers. Check the printer is switched on and connected to this PC, and added in Browser Print.</div>
        )}
        {printers && printers.length > 0 && (
          <select className="styled-input" value={uid} onChange={(e) => setUid(e.target.value)} style={{ width: '100%' }}>
            {printers.map((p) => <option key={p.uid} value={p.uid}>{p.name}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={() => onDone(chosen)} disabled={!chosen} style={reportButtonStyle(!chosen)}>Use This Printer</button>
          <CancelButton onClick={() => onDone(null)} />
        </div>
      </div>
    </div>
  );
}
